import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles, X } from "lucide-react";
import DonationAlertSettings from "@/components/DonationAlertSettings";

/**
 * Admin manager for the AI-video "Characters" whose style is prepended to a
 * donor's video prompt (e.g. Racist Pig, Rageful Cow, Peaceful Duck…).
 */
export default function DonationCharactersAdmin() {
  const [chars, setChars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", style_prompt: "", active: true });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/donation-characters");
      setChars(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/donation-characters", form);
      toast.success("Character added");
      setForm({ name: "", style_prompt: "", active: true });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setCreating(false); }
  };

  const saveOne = async (c) => {
    try {
      await api.put(`/admin/donation-characters/${c.id}`, { name: c.name, style_prompt: c.style_prompt, active: c.active });
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this character?")) return;
    try {
      await api.delete(`/admin/donation-characters/${id}`);
      toast.success("Deleted");
      setChars((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const patch = (id, key, val) => setChars((cs) => cs.map((c) => (c.id === id ? { ...c, [key]: val } : c)));

  return (
    <div className="space-y-8" data-testid="donation-characters-admin">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2"><Sparkles size={12} /> ▮ DONATION ALERTS · AI CHARACTERS</div>
        <p className="text-[var(--ff-text-dim)] text-sm font-mono-ui max-w-2xl">
          Each character&apos;s style prompt is prepended to a donor&apos;s video prompt before it&apos;s sent to the AI video model. Keep style prompts vivid and specific.
        </p>
      </div>

      {/* Phase 3 — Discord voice + text donation alerts */}
      <DonationAlertSettings />

      <div className="border-t border-[var(--ff-border,#2a2a2a)] pt-2">
        <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2"><Sparkles size={12} /> ▮ AI CHARACTERS</div>
      </div>

      {/* Add new */}
      <div className="surface-card p-6" data-testid="character-create-card">
        <div className="label-eyebrow mb-4">▮ ADD CHARACTER</div>
        <div className="grid md:grid-cols-2 gap-4">
          <input
            type="text"
            value={form.name}
            maxLength={60}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (e.g. Peaceful Duck)"
            className="px-4 py-3 text-sm"
            data-testid="character-new-name"
          />
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="character-new-active" />
            <span className="text-sm">Active (selectable by donors)</span>
          </label>
        </div>
        <textarea
          value={form.style_prompt}
          maxLength={400}
          rows={2}
          onChange={(e) => setForm({ ...form, style_prompt: e.target.value })}
          placeholder="Style prompt, e.g. a calm serene cartoon duck meditating by a pond, soft pastel 3D animated style"
          className="w-full px-4 py-3 text-sm font-mono-ui mt-4"
          data-testid="character-new-style"
        />
        <div className="flex justify-end mt-4">
          <button onClick={create} disabled={creating} className="btn-primary" data-testid="character-create-btn"><Plus size={14} /> {creating ? "Adding…" : "Add Character"}</button>
        </div>
      </div>

      {/* Existing */}
      {loading ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>
      ) : chars.length === 0 ? (
        <div className="surface-card p-8 text-center text-[var(--ff-text-dim)] text-sm font-mono-ui">No characters yet.</div>
      ) : (
        <div className="space-y-4">
          {chars.map((c) => (
            <div key={c.id} className="surface-card p-5" data-testid={`character-row-${c.id}`}>
              <div className="grid md:grid-cols-2 gap-4 mb-3">
                <input type="text" value={c.name} maxLength={60} onChange={(e) => patch(c.id, "name", e.target.value)} className="px-4 py-2 text-sm font-display text-lg" data-testid={`character-name-${c.id}`} />
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!c.active} onChange={(e) => patch(c.id, "active", e.target.checked)} className="w-4 h-4 accent-[var(--ff-red)]" data-testid={`character-active-${c.id}`} />
                  <span className="text-sm">{c.active ? "Active" : "Hidden"}</span>
                </label>
              </div>
              <textarea value={c.style_prompt || ""} maxLength={400} rows={2} onChange={(e) => patch(c.id, "style_prompt", e.target.value)} className="w-full px-4 py-2 text-sm font-mono-ui" data-testid={`character-style-${c.id}`} />
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => remove(c.id)} className="btn-ghost text-xs text-[var(--ff-text-dim)] hover:text-white" data-testid={`character-delete-${c.id}`}><Trash2 size={12} /> Delete</button>
                <button onClick={() => saveOne(c)} className="btn-primary text-xs" data-testid={`character-save-${c.id}`}><Save size={12} /> Save</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
